import {
  OpenPanel,
  type OpenPanelOptions,
} from '@openpanel/web';

type TrackProperties = Record<string, unknown>;
type IdentifyPayload = {
  profileId: string;
  email?: string;
  firstName?: string;
  properties?: Record<string, unknown>;
};
type IncrementPayload = {
  profileId: string;
  property: string;
  value?: number;
};
type DecrementPayload = {
  profileId: string;
  property: string;
  value?: number;
};

type InitOverrides = Partial<Omit<OpenPanelOptions, 'clientId'>> & {
  clientId?: string;
};

class OpenPanelService {
  private static _instance: OpenPanelService | null = null;
  private client: OpenPanel | null = null;
  private initialized = false;

  private constructor() { }

  static getInstance(): OpenPanelService {
    if (!OpenPanelService._instance) {
      OpenPanelService._instance = new OpenPanelService();
    }
    return OpenPanelService._instance;
  }

  init(overrides: InitOverrides = {}): void {
    if (this.initialized) return;

    const clientId =
      overrides.clientId ?? process.env.REACT_APP_OPENPANEL_CLIENT_ID;

    if (!clientId) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(
          '[OpenPanel] REACT_APP_OPENPANEL_CLIENT_ID is not set. Analytics disabled.'
        );
      }
      return;
    }

    this.client = new OpenPanel({
      clientId,
      waitForProfile: true,
      trackScreenViews: false,
      trackOutgoingLinks: false,
      trackAttributes: false,
      disabled: true,
      sessionReplay: {
        enabled: true,
        maskAllText: false,
        maskAllInputs: false,
      },
      ...overrides,
    });

    this.initialized = true;
    // Enable recording immediately so public/applicant routes are captured too.
    this.client.ready();
  }

  isReady(): boolean {
    return this.client !== null;
  }

  getClient(): OpenPanel | null {
    return this.client;
  }

  track(name: string, properties?: TrackProperties): void {
    this.client?.track(name, properties);
  }

  identify(payload: IdentifyPayload): void {
    this.client?.identify(payload);
  }

  screenView(
    pathOrProperties?: string | TrackProperties,
    properties?: TrackProperties
  ): void {
    if (!this.client) return;
    if (typeof pathOrProperties === 'string') {
      this.client.screenView(pathOrProperties, properties);
    } else {
      this.client.screenView(pathOrProperties);
    }
  }

  setGlobalProperties(properties: Record<string, unknown>): void {
    this.client?.setGlobalProperties(properties);
  }

  increment(payload: IncrementPayload): void {
    this.client?.increment(payload);
  }

  decrement(payload: DecrementPayload): void {
    this.client?.decrement(payload);
  }

  clear(): void {
    this.client?.clear();
  }

  ready(): void {
    this.client?.ready();
  }
}

export const openPanel = OpenPanelService.getInstance();
export type { OpenPanelService };
